/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import moment from 'moment';
import IcomoonReact from 'icomoon-react';
import PropTypes from 'prop-types';

import iconSet from '../../../shared/images/teambuildr-selection.json';
import Text from '../../../shared/components/Text/Text';

const ModalContentWrapper = styled('div')`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ModalTitle = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 20px;
`;

const CloseIcon = styled('div')`
  cursor: pointer;
  transition: all 250ms ease-in-out;
  :hover {
    opacity: 0.7;
  }
`;

const SectionTitle = styled('div')`
  margin-bottom: 8px;
`;

const LinkContainer = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const LinkInputWrapper = styled('div')`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background-color: #F5F5F5;
  border-radius: 4px;
  flex-grow: 1;
  margin-right: 12px;
  height: 48px;
  overflow: hidden;
`;

const LinkIcon = styled('div')`
  display: flex;
  align-items: center;
  margin-right: 8px;
`;

const LinkInput = styled('div')`
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #9E9E9E;
`;

const InfoGrid = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  font-family: 'Nunito Sans';
`;

const InfoItem = styled('div')`
  display: flex;
  flex-direction: column;
`;

const InfoLabel = styled('div')`
  color: #9E9E9E;
  margin-bottom: 4px;
`;

const InfoValue = styled('div')`
  font-weight: 600;
`;

const CopyTextButton = styled('div')`
  background-color: #444444;
  min-width: 120px;
  height: 48px;
  color: white;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-weight: 700;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const ShareWorkoutModal = ({ closeModal, modalData }) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const programType = useSelector((state) => state.calendar.data.programType);
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const sharedWorkoutInfo = useSelector((state) => state.calendar.data.sharedWorkoutInfo);

  // Generate a unique share ID - for this example, using a mock format
  const shareId = sharedWorkoutInfo?.slug;
  const shareUrl = `${`${process.env.PHP_APP_URL}/workout/${shareId}`}`;

  const calendarName = selectedReduxCalendar?.name || '';
  const programName = selectedReduxNonDateBasedProgram?.programName || '';

  // Use weekViewDate from modalData if available, otherwise fall back to selectedDate
  const effectiveDate = (modalData?.isFromWeekView || viewType === 'monthView') && modalData?.weekViewDate
    ? modalData.weekViewDate
    : selectedDate;
  const dateFormatted = moment(effectiveDate).format('MMMM D, YYYY');

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  return (
    <ModalContentWrapper>
      <ModalTitle>
        <Text fontSize='24px' fontWeight='600'>Share Workout</Text>
        <CloseIcon onClick={closeModal}>
          <IcomoonReact iconSet={iconSet} icon='remove' size={14} color='#9E9E9E' />
        </CloseIcon>
      </ModalTitle>

      <SectionTitle>
        <Text fontSize='16px' fontWeight='600'>Calendar Link</Text>
      </SectionTitle>

      <LinkContainer>
        <LinkInputWrapper>
          <LinkIcon>
            <IcomoonReact iconSet={iconSet} icon='link' size={16} color='#9E9E9E' />
          </LinkIcon>
          <LinkInput>{shareUrl}</LinkInput>
        </LinkInputWrapper>

        <CopyTextButton
          onClick={handleCopyLink}
        >
          {linkCopied ? 'Copied!' : 'Copy Link'}
        </CopyTextButton>
      </LinkContainer>

      <InfoGrid>
        <InfoItem>
          {programType === 'free-form-program' ? (
            <InfoLabel>Program</InfoLabel>
          ) : (
            <InfoLabel>Calendar</InfoLabel>
          )}
          <InfoValue>{programType === 'calendar' ? calendarName : programName}</InfoValue>
        </InfoItem>
        <InfoItem>
          {programType === 'free-form-program' ? (
            <>
              <InfoLabel>Program Day</InfoLabel>
              <InfoValue>{`Week ${modalData?.workoutDetails?.weekNumber}-Day ${modalData?.workoutDetails?.dayInWeek}`}</InfoValue>
            </>
          ) : (
            <>
              <InfoLabel>Date</InfoLabel>
              <InfoValue>{dateFormatted}</InfoValue>
            </>
          )}
        </InfoItem>
      </InfoGrid>
    </ModalContentWrapper>
  );
};

ShareWorkoutModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  modalData: PropTypes.shape({
    weekViewDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isFromWeekView: PropTypes.bool,
  }),
};

ShareWorkoutModal.defaultProps = {
  modalData: null,
};

export default ShareWorkoutModal;
